import { useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  rectIntersection,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
  type CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { useState } from 'react';
import CardGrid from './CardGrid';
import SortableBlock from './SortableBlock';
import HeadingBlock from './HeadingBlock';
import DividerBlock from './DividerBlock';
import CardGroupBlock from './CardGroupBlock';
import AddBlockBar from './AddBlockBar';
import EmptyState from './EmptyState';
import PortalCard from './PortalCard';
import type { NavNode, BlockType } from '../../types/nav';
import type { CardMetadata } from '../../types/card';
import type { AddCardFn, SetThumbnailFn } from '../../types/addCard';

const BLOCK_PREFIX = 'block-';
const CARD_PREFIX = 'card-';
const CONTAINER_UNGROUPED = 'container-ungrouped';
const CONTAINER_PREFIX = 'container-';

interface Props {
  sectionId: string;
  blocks: NavNode[];
  pages: NavNode[];
  cards: CardMetadata[];
  onAddCard?: AddCardFn;
  onUpdateCard?: (id: string, updates: { title: string; summary: string; url: string }) => Promise<void>;
  onSetThumbnail?: SetThumbnailFn;
  onDeleteCard?: (id: string) => Promise<void>;
  onAddBlock: (blockType: BlockType, title: string) => Promise<void>;
  onUpdateBlock: (blockId: string, title: string) => Promise<void>;
  onDeleteBlock: (blockId: string) => Promise<void>;
  onMovePageToBlock: (pageId: string, blockId: string | null) => Promise<void>;
  onReorderBlocks: (blockIds: string[]) => Promise<void>;
  loading?: boolean;
}

/**
 * Custom collision detection: use closestCenter for block drags,
 * pointerWithin (then rectIntersection fallback) for card drags.
 */
function createCollisionDetection(): CollisionDetection {
  return (args) => {
    const activeId = String(args.active.id);
    if (activeId.startsWith(BLOCK_PREFIX)) {
      return closestCenter(args);
    }
    // For cards, prefer pointerWithin (finds the container the pointer is inside),
    // then fall back to rectIntersection.
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return rectIntersection(args);
  };
}

const collisionDetection = createCollisionDetection();

export default function SectionContent({
  sectionId,
  blocks,
  pages,
  cards,
  onAddCard,
  onUpdateCard,
  onSetThumbnail,
  onDeleteCard,
  onAddBlock,
  onUpdateBlock,
  onDeleteBlock,
  onMovePageToBlock,
  onReorderBlocks,
  loading,
}: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Separate ungrouped pages (no blockId) from grouped ones
  const ungroupedPages = useMemo(
    () => pages.filter((p) => p.parentId === sectionId && !p.blockId),
    [pages, sectionId],
  );

  const ungroupedCardIds = useMemo(
    () => new Set(ungroupedPages.map((p) => p.cardId).filter(Boolean)),
    [ungroupedPages],
  );

  const ungroupedCards = useMemo(
    () => cards.filter((c) => ungroupedCardIds.has(c.id)),
    [cards, ungroupedCardIds],
  );

  // Sort blocks by order
  const sortedBlocks = useMemo(
    () => [...blocks].sort((a, b) => a.order - b.order),
    [blocks],
  );

  // Build a map from cardId -> pageId for quick lookup when moving cards
  const cardIdToPageId = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of pages) {
      if (p.cardId) map.set(p.cardId, p.id);
    }
    return map;
  }, [pages]);

  // IDs for block-level sortable context (prefixed)
  const blockSortableIds = useMemo(
    () => sortedBlocks.map((b) => `${BLOCK_PREFIX}${b.id}`),
    [sortedBlocks],
  );

  // Card IDs for ungrouped container (prefixed with card-)
  const ungroupedCardSortableIds = useMemo(
    () => ungroupedCards.map((c) => `${CARD_PREFIX}${c.id}`),
    [ungroupedCards],
  );

  // Find which container a card-prefixed ID belongs to
  const findCardContainer = useCallback(
    (cardPrefixedId: string): string | null => {
      const cardId = cardPrefixedId.replace(CARD_PREFIX, '');
      // Check ungrouped
      if (ungroupedCardIds.has(cardId)) return CONTAINER_UNGROUPED;
      // Check each card-group block
      for (const block of sortedBlocks) {
        if (block.blockType !== 'card-group') continue;
        const groupPages = pages.filter(
          (p) => p.parentId === sectionId && p.blockId === block.id,
        );
        const groupCardIds = new Set(groupPages.map((p) => p.cardId).filter(Boolean));
        if (groupCardIds.has(cardId)) return `${CONTAINER_PREFIX}${block.id}`;
      }
      return null;
    },
    [ungroupedCardIds, sortedBlocks, pages, sectionId],
  );

  // Drag overlay card (shown while dragging)
  const activeCard = useMemo(() => {
    if (!activeId || !activeId.startsWith(CARD_PREFIX)) return null;
    const cardId = activeId.replace(CARD_PREFIX, '');
    return cards.find((c) => c.id === cardId) ?? null;
  }, [activeId, cards]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      console.log('[DragEnd]', { activeId: active.id, overId: over?.id });
      if (!over) return;

      const activeIdStr = String(active.id);
      const overIdStr = String(over.id);

      // --- Block reordering ---
      if (activeIdStr.startsWith(BLOCK_PREFIX)) {
        if (!overIdStr.startsWith(BLOCK_PREFIX)) return;
        if (activeIdStr === overIdStr) return;

        const activeBlockId = activeIdStr.replace(BLOCK_PREFIX, '');
        const overBlockId = overIdStr.replace(BLOCK_PREFIX, '');
        const oldIndex = sortedBlocks.findIndex((b) => b.id === activeBlockId);
        const newIndex = sortedBlocks.findIndex((b) => b.id === overBlockId);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(sortedBlocks, oldIndex, newIndex);
        const newIds = reordered.map((b) => b.id);
        console.log('[DragEnd] Reorder blocks:', { oldIndex, newIndex, newIds });
        onReorderBlocks(newIds);
        return;
      }

      // --- Card cross-container movement ---
      if (activeIdStr.startsWith(CARD_PREFIX)) {
        const cardId = activeIdStr.replace(CARD_PREFIX, '');
        const pageId = cardIdToPageId.get(cardId);
        if (!pageId) return;

        // Determine target container
        let targetContainer: string | null = null;

        if (overIdStr.startsWith(CONTAINER_PREFIX) || overIdStr === CONTAINER_UNGROUPED) {
          // Dropped directly on a container
          targetContainer = overIdStr;
        } else if (overIdStr.startsWith(CARD_PREFIX)) {
          // Dropped on another card -- find which container that card is in
          targetContainer = findCardContainer(overIdStr);
        }

        if (!targetContainer) return;

        const sourceContainer = findCardContainer(activeIdStr);

        // Same container: reorder cards within the group
        if (sourceContainer === targetContainer && overIdStr.startsWith(CARD_PREFIX)) {
          const overCardId = overIdStr.replace(CARD_PREFIX, '');
          if (cardId === overCardId) return;

          // Get the pages in this container, sorted by order
          const containerBlockId = targetContainer === CONTAINER_UNGROUPED
            ? undefined
            : targetContainer.replace(CONTAINER_PREFIX, '');

          const containerPages = pages
            .filter((p) =>
              p.parentId === sectionId &&
              (containerBlockId ? p.blockId === containerBlockId : !p.blockId)
            )
            .sort((a, b) => a.order - b.order);

          const activePageId = cardIdToPageId.get(cardId);
          const overPageId = cardIdToPageId.get(overCardId);
          if (!activePageId || !overPageId) return;

          const oldIdx = containerPages.findIndex((p) => p.id === activePageId);
          const newIdx = containerPages.findIndex((p) => p.id === overPageId);
          if (oldIdx === -1 || newIdx === -1) return;

          const reordered = arrayMove(containerPages, oldIdx, newIdx);
          onReorderBlocks(reordered.map((p) => p.id));
          return;
        }

        if (sourceContainer === targetContainer) return;

        // Determine target blockId
        let targetBlockId: string | null = null;
        if (targetContainer === CONTAINER_UNGROUPED) {
          targetBlockId = null;
        } else if (targetContainer.startsWith(CONTAINER_PREFIX)) {
          targetBlockId = targetContainer.replace(CONTAINER_PREFIX, '');
        }

        console.log('[DragEnd] Move card:', { pageId, targetBlockId, sourceContainer, targetContainer });
        onMovePageToBlock(pageId, targetBlockId);
      }
    },
    [sortedBlocks, onReorderBlocks, cardIdToPageId, findCardContainer, onMovePageToBlock],
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  // If no blocks and no ungrouped cards, show backward-compatible flat view
  const hasNoBlocks = blocks.length === 0;

  if (hasNoBlocks) {
    // Backward-compatible mode: render all cards in a flat grid (existing behavior)
    return (
      <Box>
        <CardGrid
          cards={cards}
          loading={!!loading}
          hasSection
          onAddCard={onAddCard}
          onUpdateCard={onUpdateCard}
          onSetThumbnail={onSetThumbnail}
          onDeleteCard={onDeleteCard}
        />
        <AddBlockBar onAddBlock={onAddBlock} />
      </Box>
    );
  }

  // Structured mode: ungrouped first, then blocks in order
  const hasUngroupedCards = ungroupedCards.length > 0;
  const hasAnyContent = hasUngroupedCards || blocks.length > 0;

  if (!hasAnyContent && !loading) {
    return (
      <Box>
        <EmptyState hasSection />
        <AddBlockBar onAddBlock={onAddBlock} />
      </Box>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <Box>
        {/* Ungrouped cards first -- droppable container */}
        {(hasUngroupedCards || onAddCard) && (
          <Box sx={{ mb: sortedBlocks.length > 0 ? 3 : 0 }}>
            <CardGrid
              cards={ungroupedCards}
              loading={!!loading}
              hasSection
              onAddCard={onAddCard}
              onUpdateCard={onUpdateCard}
              onSetThumbnail={onSetThumbnail}
              onDeleteCard={onDeleteCard}
              containerId={CONTAINER_UNGROUPED}
              sortableCardIds={ungroupedCardSortableIds}
              isDragActive={!!activeId && activeId.startsWith(CARD_PREFIX)}
            />
          </Box>
        )}

        {/* Blocks in order -- sortable */}
        <SortableContext
          items={blockSortableIds}
          strategy={verticalListSortingStrategy}
        >
          {sortedBlocks.map((block) => {
            switch (block.blockType) {
              case 'heading':
                return (
                  <SortableBlock key={block.id} id={`${BLOCK_PREFIX}${block.id}`}>
                    {(dragHandleProps) => (
                      <HeadingBlock
                        blockId={block.id}
                        title={block.title}
                        onUpdate={onUpdateBlock}
                        onDelete={onDeleteBlock}
                        dragHandleProps={dragHandleProps}
                      />
                    )}
                  </SortableBlock>
                );

              case 'divider':
                return (
                  <SortableBlock key={block.id} id={`${BLOCK_PREFIX}${block.id}`}>
                    {(dragHandleProps) => (
                      <DividerBlock
                        blockId={block.id}
                        onDelete={onDeleteBlock}
                        dragHandleProps={dragHandleProps}
                      />
                    )}
                  </SortableBlock>
                );

              case 'card-group': {
                // Pages belonging to this card-group block
                const groupPages = pages.filter(
                  (p) => p.parentId === sectionId && p.blockId === block.id,
                );
                const groupCardIds = groupPages.map((p) => p.cardId).filter(Boolean) as string[];
                const groupCards = cards.filter((c) => groupCardIds.includes(c.id));
                const groupSortableIds = groupCards.map((c) => `${CARD_PREFIX}${c.id}`);

                return (
                  <SortableBlock key={block.id} id={`${BLOCK_PREFIX}${block.id}`}>
                    {(dragHandleProps) => (
                      <CardGroupBlock
                        block={block}
                        pages={groupPages}
                        cards={cards}
                        onAddCard={onAddCard}
                        onUpdateCard={onUpdateCard}
                        onSetThumbnail={onSetThumbnail}
                        onDeleteCard={onDeleteCard}
                        onUpdate={onUpdateBlock}
                        onDelete={onDeleteBlock}
                        dragHandleProps={dragHandleProps}
                        containerId={`${CONTAINER_PREFIX}${block.id}`}
                        sortableCardIds={groupSortableIds}
                        isDragActive={!!activeId && activeId.startsWith(CARD_PREFIX)}
                      />
                    )}
                  </SortableBlock>
                );
              }

              default:
                return null;
            }
          })}
        </SortableContext>

        <AddBlockBar onAddBlock={onAddBlock} />
      </Box>

      {/* Drag overlay for cards */}
      <DragOverlay dropAnimation={null}>
        {activeCard ? (
          <Box sx={{ width: 300, opacity: 0.85, pointerEvents: 'none' }}>
            <PortalCard card={activeCard} />
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
